/**
 * Google Analytics 4 for the app, with consent denied until the athlete says otherwise.
 *
 * Two things a naive `<script>` paste gets wrong here, both of which this file
 * exists to handle:
 *
 * 1. **Consent.** Mavinty sells to UK and EU athletes, and analytics cookies are
 *    not "strictly necessary", so they need consent regardless of the user being
 *    signed in. Consent Mode v2 is initialised to `denied` in index.html before
 *    the tag loads; nothing is stored or sent until `grantAnalytics()` runs.
 *
 * 2. **It is a single-page app.** GA records one `page_view` on the initial load
 *    and then nothing, because React Router changes the URL without a document
 *    load. Every route change has to be reported by hand — otherwise the whole
 *    property looks like every visitor read one screen and left.
 *
 * The choice is stored per-origin. The marketing site keeps its own consent under
 * its own domain; localStorage is not shared across mavinty.com and
 * app.mavinty.com, so the two cannot be merged without a cross-domain handshake.
 */

export const MEASUREMENT_ID = "G-1C2GRG611Y";
const STORAGE_KEY = "mavinty_analytics_consent";

type Consent = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

/** What the athlete previously chose, or null if they have not been asked. */
export function storedConsent(): Consent | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null; // Safari private mode throws rather than returning null.
  }
}

function persist(value: Consent): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* A refused write must not break the app; the default stays denied. */
  }
}

/**
 * Inject gtag.js. Deliberately not in index.html: Consent Mode still emits a
 * cookieless ping while consent is denied, and one was observed reaching Google
 * before the athlete had answered. Nothing is requested until this runs.
 *
 * No Subresource Integrity hash: Google generates gtag.js per property and
 * rotates it, publishes no hash, and an `integrity` attribute would simply
 * block the script. The exposure is bounded instead by loading it only after
 * consent, from Google's own origin over TLS.
 */
function loadTag(): void {
  if (document.getElementById("ga4-loader")) return;
  const s = document.createElement("script");
  s.id = "ga4-loader";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

export function grantAnalytics(): void {
  persist("granted");
  loadTag();
  gtag("consent", "update", {
    analytics_storage: "granted",
  });
  // The initial page_view was suppressed while consent was denied, so send the
  // current screen now rather than waiting for the next navigation.
  trackPageView(window.location.pathname + window.location.search);
}

export function denyAnalytics(): void {
  persist("denied");
  gtag("consent", "update", { analytics_storage: "denied" });
}

/** Re-apply a previous choice on boot, before any page_view is sent. */
export function restoreConsent(): void {
  if (storedConsent() === "granted") {
    loadTag();
    gtag("consent", "update", { analytics_storage: "granted" });
  }
}

/**
 * Report a screen. Route paths are sent rather than full URLs, and ids are
 * masked: /workout/8412 and /chat/33 would otherwise become thousands of
 * distinct pages in the report, and a meal or workout id is personal data we
 * have no reason to hand to Google.
 */
export function trackPageView(path: string): void {
  if (storedConsent() !== "granted") return;
  gtag("event", "page_view", {
    page_path: maskIds(path),
    page_location: window.location.origin + maskIds(path),
    page_title: document.title,
  });
}

export function maskIds(path: string): string {
  return path
    .replace(/\/\d+(?=\/|$)/g, "/:id")
    .replace(/token=[^&]+/g, "token=:token");
}
